import type {
  SessionBootstrapResponse
} from "@camp-points/shared";
import {
  onAuthStateChanged
} from "firebase/auth";
import {
  useEffect,
  useState
} from "react";
import { LoadingScreen } from "./components/LoadingScreen";
import {
  getSessionBootstrap
} from "./lib/api";
import { auth } from "./lib/firebase";
import {
  getOrganizationSlug
} from "./lib/route";
import { CampCodePage } from "./pages/CampCodePage";
import { CampSwitcherPage } from "./pages/CampSwitcherPage";
import { ChangePinPage } from "./pages/ChangePinPage";
import { StaffApp } from "./features/staff/StaffApp";
import { AdminApp } from "./features/admin/AdminApp";
import { LoginPage } from "./pages/LoginPage";

type View =
  | "loading"
  | "login"
  | "switcher"
  | "changePin"
  | "dashboard"
  | "admin";

export default function App() {
  const [slug, setSlug] =
    useState(getOrganizationSlug());

  const [view, setView] =
    useState<View>("loading");

  const [
    selectedOrganizationId,
    setSelectedOrganizationId
  ] = useState<string>();

  const [session, setSession] =
    useState<SessionBootstrapResponse | null>(
      null
    );

  const [error, setError] =
    useState("");

  useEffect(() => {
    const listener = () => {
      setSlug(getOrganizationSlug());
    };

    window.addEventListener(
      "popstate",
      listener
    );

    return () =>
      window.removeEventListener(
        "popstate",
        listener
      );
  }, []);

  useEffect(() => {
    return onAuthStateChanged(
      auth,
      async (user) => {
        if (!slug) {
          setView("login");
          return;
        }

        if (!user) {
          setSession(null);
          setView("login");
          return;
        }

        if (!session) {
          await loadSession();
        }
      }
    );
  }, [slug]);

  async function loadSession(
    organizationId?: string
  ) {
    setView("loading");
    setError("");

    try {
      const next =
        await getSessionBootstrap(
          organizationId
        );

      setSession(next);
      setSelectedOrganizationId(
        next.activeOrganization
          .organizationId
      );

      if (
        next.activeOrganization
          .mustChangePin
      ) {
        setView("changePin");
      } else if (
        !organizationId &&
        next.organizations.length > 1
      ) {
        setView("switcher");
      } else {
        setView("dashboard");
      }
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The session could not be loaded."
      );
      setView("login");
    }
  }

  if (!slug) {
    return <CampCodePage />;
  }

  if (view === "loading") {
    return (
      <LoadingScreen message="Loading camp…" />
    );
  }

  if (
    view === "login" ||
    !session
  ) {
    return (
      <>
        <LoginPage
          slug={slug}
          onAuthenticated={async (
            organizationId
          ) => {
            setSelectedOrganizationId(
              organizationId
            );
            await loadSession(
              organizationId
            );
          }}
        />

        {error && (
          <div className="floating-error">
            {error}
          </div>
        )}
      </>
    );
  }

  if (view === "switcher") {
    return (
      <CampSwitcherPage
        organizations={
          session.organizations
        }
        onSelect={async (
          organizationId
        ) => {
          setSelectedOrganizationId(
            organizationId
          );
          await loadSession(
            organizationId
          );
        }}
      />
    );
  }

  if (view === "changePin") {
    return (
      <ChangePinPage
        organization={
          session.activeOrganization
        }
        forced={
          session.activeOrganization
            .mustChangePin
        }
        onComplete={async () => {
          await loadSession(
            session.activeOrganization
              .organizationId
          );
        }}
      />
    );
  }

  if (view === "admin") {
    return (
      <AdminApp
        session={session}
        onExit={async () => {
          await loadSession(session.activeOrganization.organizationId);
        }}
        onReloadSession={async () => {
          await loadSession(session.activeOrganization.organizationId);
          setView("admin");
        }}
      />
    );
  }

  return (
    <StaffApp
      session={session}
      onSwitchCamp={() =>
        setView("switcher")
      }
      onChangePin={() =>
        setView("changePin")
      }
      onOpenAdmin={() => setView("admin")}
    />
  );
}
